import { Component, signal, inject, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { Router } from '@angular/router';
import { NgFor, NgIf } from '@angular/common';

interface DashboardCard {
  title: string;
  value: string | number;
  icon: string;
  color: string;
  bgColor: string;
  route: string;
  description: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

interface RecentActivity {
  id: string;
  type: 'leave' | 'timesheet' | 'employee' | 'document';
  title: string;
  description: string;
  time: string;
  icon: string;
  color: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [NgFor, NgIf],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <div class="p-6 bg-gray-50 min-h-screen">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p class="text-gray-600">Welcome back! Here's what's happening in your organization today.</p>
      </div>

      <!-- Analytics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div
          *ngFor="let card of dashboardCards()"
          (click)="navigateToRoute(card.route)"
          class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 cursor-pointer transform transition-all duration-200 hover:shadow-lg hover:scale-105">

          <div class="flex items-center justify-between mb-4">
            <div [class]="card.bgColor + ' p-3 rounded-lg'">
              <sl-icon [name]="card.icon" [class]="card.color + ' text-xl'"></sl-icon>
            </div>
            <div *ngIf="card.trend" class="flex items-center text-sm">
              <sl-icon
                [name]="card.trend.isPositive ? 'trending-up' : 'trending-down'"
                [class]="card.trend.isPositive ? 'text-green-500' : 'text-red-500'">
              </sl-icon>
              <span [class]="card.trend.isPositive ? 'text-green-600' : 'text-red-600'" class="ml-1 font-medium">
                {{ card.trend.value }}%
              </span>
            </div>
          </div>

          <div class="mb-2">
            <h3 class="text-2xl font-bold text-gray-900">{{ card.value }}</h3>
            <p class="text-sm font-medium text-gray-600">{{ card.title }}</p>
          </div>

          <p class="text-xs text-gray-500">{{ card.description }}</p>
        </div>
      </div>

      <!-- Quick Actions & Recent Activity -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

        <!-- Quick Actions -->
        <div class="lg:col-span-1">
          <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <sl-icon name="zap" class="text-blue-500 mr-2"></sl-icon>
              Quick Actions
            </h2>

            <div class="space-y-3">
              <button
                *ngFor="let action of quickActions()"
                (click)="navigateToRoute(action.route)"
                class="w-full flex items-center p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 group">

                <div [class]="action.bgColor + ' p-2 rounded-lg mr-3 group-hover:scale-110 transition-transform duration-200'">
                  <sl-icon [name]="action.icon" [class]="action.color + ' text-sm'"></sl-icon>
                </div>

                <div class="text-left">
                  <p class="font-medium text-gray-900 text-sm">{{ action.title }}</p>
                  <p class="text-xs text-gray-500">{{ action.description }}</p>
                </div>

                <sl-icon name="chevron-right" class="text-gray-400 ml-auto group-hover:text-blue-500 transition-colors duration-200"></sl-icon>
              </button>
            </div>
          </div>
        </div>

        <!-- Recent Activity -->
        <div class="lg:col-span-2">
          <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <sl-icon name="activity" class="text-green-500 mr-2"></sl-icon>
              Recent Activity
            </h2>

            <div class="space-y-4">
              <div
                *ngFor="let activity of recentActivities()"
                class="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">

                <div [class]="activity.color + ' p-2 rounded-lg mr-3 flex-shrink-0'">
                  <sl-icon [name]="activity.icon" class="text-white text-sm"></sl-icon>
                </div>

                <div class="flex-1 min-w-0">
                  <p class="font-medium text-gray-900 text-sm">{{ activity.title }}</p>
                  <p class="text-xs text-gray-500 mt-1">{{ activity.description }}</p>
                </div>

                <span class="text-xs text-gray-400 flex-shrink-0">{{ activity.time }}</span>
              </div>
            </div>

            <div class="mt-4 pt-4 border-t border-gray-200">
              <button class="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center">
                View all activity
                <sl-icon name="arrow-right" class="ml-1 text-xs"></sl-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class DashboardComponent {
  private router = inject(Router);

  dashboardCards = signal<DashboardCard[]>([
    {
      title: 'Total Employees',
      value: 247,
      icon: 'people',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      route: '/employees',
      description: 'Active employees in organization',
      trend: { value: 12, isPositive: true }
    },
    {
      title: 'On Leave Today',
      value: 8,
      icon: 'calendar-x',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      route: '/leave-requests',
      description: 'Employees currently on leave',
      trend: { value: 5, isPositive: false }
    },
    {
      title: 'Clocked In Today',
      value: 189,
      icon: 'clock',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      route: '/timesheets',
      description: 'Employees who clocked in today',
      trend: { value: 8, isPositive: true }
    },
    {
      title: 'Pending Approvals',
      value: 23,
      icon: 'clipboard-check',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      route: '/timesheets?filter=pending',
      description: 'Timesheet & leave approvals',
      trend: { value: 15, isPositive: false }
    }
  ]);

  quickActions = signal([
    {
      title: 'Add New Employee',
      description: 'Register a new team member',
      icon: 'user-plus',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      route: '/employees/add'
    },
    {
      title: 'Create Rota',
      description: 'Schedule shifts for the team',
      icon: 'calendar-plus',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      route: '/rota-planner/create'
    },
    {
      title: 'Review Timesheets',
      description: 'Approve pending timesheets',
      icon: 'file-text',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      route: '/timesheets?filter=pending'
    },
    {
      title: 'Generate Report',
      description: 'Create analytics reports',
      icon: 'bar-chart',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      route: '/reports'
    }
  ]);

  recentActivities = signal<RecentActivity[]>([
    {
      id: '1',
      type: 'employee',
      title: 'New Employee Added',
      description: 'Sarah Johnson joined the Marketing team',
      time: '2 hours ago',
      icon: 'user-plus',
      color: 'bg-blue-500'
    },
    {
      id: '2',
      type: 'leave',
      title: 'Leave Request Approved',
      description: 'John Smith\'s vacation request for next week',
      time: '4 hours ago',
      icon: 'check-circle',
      color: 'bg-green-500'
    },
    {
      id: '3',
      type: 'timesheet',
      title: 'Timesheet Submitted',
      description: 'Emma Wilson submitted weekly timesheet',
      time: '6 hours ago',
      icon: 'clock',
      color: 'bg-purple-500'
    },
    {
      id: '4',
      type: 'document',
      title: 'Document Uploaded',
      description: 'New policy document added to HR folder',
      time: '1 day ago',
      icon: 'file-plus',
      color: 'bg-orange-500'
    }
  ]);

  navigateToRoute(route: string): void {
    this.router.navigate([route]);
  }
}
